/**
 * Audit Log View (Command-Net/HUD): DB-gestütztes `audit_log` durchsuchbar mit
 * Filtern, Result-/Action-Chips und einer Detailansicht (metadata_json lesbar).
 * Reine Logik liegt in ../audit-log.ts. Sensitive Felder (IP/User-Agent) liefert
 * die API nur mit action.view_sensitive — die UI verlässt sich nicht aufs
 * Verstecken, sondern zeigt nur, was der Server herausgibt.
 */

import { useSignal, useSignalEffect } from "@preact/signals";
import type { ComponentChildren } from "preact";
import { api } from "../store";
import { PageHead, Panel } from "../components";
import { t } from "../i18n";
import {
  AUDIT_RANGES,
  AUDIT_RESULTS,
  auditActionGroup,
  auditResultClass,
  buildAuditQuery,
  emptyAuditFilters,
  formatAuditTs,
  matchesAuditText,
  prettyMetadata,
  targetLabel,
  type AuditEntry,
  type AuditFilters,
  type AuditRange,
  type AuditResponse,
} from "../audit-log";

/** Übersetztes Label für einen Zeitraum-Filter (Konstante bleibt sprachneutral/testbar). */
function auditRangeLabel(id: AuditRange): string {
  switch (id) {
    case "all": return t("audit.all");
    case "1h": return t("audit.range1h");
    case "24h": return t("audit.range24h");
    case "7d": return t("audit.range7d");
    case "30d": return t("audit.range30d");
  }
}

function ResultChip({ result }: { result: AuditEntry["result"] }) {
  return <span class={`audit-chip res ${auditResultClass(result)}`}>{result}</span>;
}

function ActionChip({ action }: { action: string }) {
  return (
    <span class={`audit-chip act grp-${auditActionGroup(action)}`} title={action}>
      {action}
    </span>
  );
}

function DetailRow({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <div class="audit-detail-row">
      <span class="label">{label}</span>
      <div class="audit-detail-val">{children}</div>
    </div>
  );
}

function DetailPanel({ entry, canViewSensitive }: { entry: AuditEntry | null; canViewSensitive: boolean }) {
  if (!entry) {
    return (
      <Panel title={t("audit.entry")}>
        <div class="empty audit-empty">{t("audit.selectRow")}</div>
      </Panel>
    );
  }
  const meta = prettyMetadata(entry.metadata);
  return (
    <Panel
      title={<span>{t("audit.entry")} <span class="muted mono">#{entry.id}</span></span>}
      actions={<ResultChip result={entry.result} />}
    >
      <div class="audit-detail">
        <DetailRow label={t("audit.time")}><span class="mono">{formatAuditTs(entry.ts)}</span></DetailRow>
        <DetailRow label={t("field.action")}><ActionChip action={entry.action} /></DetailRow>
        <DetailRow label={t("audit.result")}><ResultChip result={entry.result} /></DetailRow>
        <DetailRow label={t("audit.actor")}>
          {entry.actorUsername || "—"}
          {entry.actorRole ? <span class="muted"> · {entry.actorRole}</span> : null}
        </DetailRow>
        <DetailRow label="Server">{entry.serverId || "—"}</DetailRow>
        <DetailRow label={t("audit.target")}><span class="mono">{targetLabel(entry)}</span></DetailRow>
        {canViewSensitive && (
          <>
            <DetailRow label="IP"><span class="mono">{entry.actorIp || "—"}</span></DetailRow>
            <DetailRow label="User-Agent"><span class="audit-ua">{entry.actorUserAgent || "—"}</span></DetailRow>
          </>
        )}
        {entry.summary && <DetailRow label={t("audit.summary")}><span class="audit-summary">{entry.summary}</span></DetailRow>}
        {entry.error && <DetailRow label={t("common.error")}><span class="audit-error">{entry.error}</span></DetailRow>}
      </div>

      <div class="audit-section">
        <h3>metadata_json</h3>
        {meta ? (
          <pre class="audit-meta mono">{meta}</pre>
        ) : (
          <div class="empty audit-empty">{t("audit.noMetadata")}</div>
        )}
        {!canViewSensitive && (
          <div class="audit-hint muted">{t("audit.sensitiveHintPre")} <code>[redacted]</code> {t("audit.sensitiveHintPost")}</div>
        )}
      </div>
    </Panel>
  );
}

export function AuditLog() {
  const filters = useSignal<AuditFilters>(emptyAuditFilters());
  const entries = useSignal<AuditEntry[]>([]);
  const selected = useSignal<AuditEntry | null>(null);
  const canViewSensitive = useSignal(false);
  const loading = useSignal(false);
  const error = useSignal("");

  const set = <K extends keyof AuditFilters>(key: K, value: AuditFilters[K]) => {
    filters.value = { ...filters.value, [key]: value };
  };

  async function load() {
    loading.value = true;
    error.value = "";
    try {
      const body = buildAuditQuery(filters.value, Math.floor(Date.now() / 1000));
      const r = await api.call<AuditResponse>("audit_log", body);
      if (r.ok) {
        entries.value = Array.isArray(r.entries) ? r.entries : [];
        canViewSensitive.value = !!r.canViewSensitive;
        if (selected.value && !entries.value.some((e) => e.id === selected.value!.id)) selected.value = null;
      } else {
        error.value = r.error ?? t("audit.loadFailed");
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : t("audit.loadFailed");
    } finally {
      loading.value = false;
    }
  }

  useSignalEffect(() => { void load(); });

  const visible = entries.value.filter((e) => matchesAuditText(e, filters.value.text));

  return (
    <div class="view-enter audit-view">
      <PageHead
        title={t("view.auditLog")}
        sub={t("audit.sub")}
      >
        <button class="btn sm" onClick={() => void load()} disabled={loading.value}>↻ {t("common.refresh")}</button>
      </PageHead>

      <div class="audit-layout">
        <Panel title={t("audit.filter")}>
          <form
            class="audit-filters"
            onSubmit={(e) => { e.preventDefault(); void load(); }}
          >
            <label>
              <span class="label">{t("field.action")}</span>
              <input class="input" value={filters.value.action} placeholder={t("audit.actionPlaceholder")}
                onInput={(e) => set("action", (e.target as HTMLInputElement).value)} />
            </label>
            <label>
              <span class="label">{t("audit.result")}</span>
              <select class="select" value={filters.value.result}
                onChange={(e) => set("result", (e.target as HTMLSelectElement).value as AuditFilters["result"])}>
                <option value="">{t("audit.all")}</option>
                {AUDIT_RESULTS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label>
              <span class="label">{t("audit.actor")}</span>
              <input class="input" value={filters.value.actor} placeholder={t("audit.actorPlaceholder")}
                onInput={(e) => set("actor", (e.target as HTMLInputElement).value)} />
            </label>
            <label>
              <span class="label">{t("audit.serverId")}</span>
              <input class="input" value={filters.value.serverId} placeholder={t("audit.serverIdPlaceholder")}
                onInput={(e) => set("serverId", (e.target as HTMLInputElement).value)} />
            </label>
            <label>
              <span class="label">{t("audit.range")}</span>
              <select class="select" value={filters.value.range}
                onChange={(e) => set("range", (e.target as HTMLSelectElement).value as AuditFilters["range"])}>
                {AUDIT_RANGES.map((r) => <option key={r.id} value={r.id}>{auditRangeLabel(r.id)}</option>)}
              </select>
            </label>
            <label>
              <span class="label">Limit</span>
              <select class="select" value={String(filters.value.limit)}
                onChange={(e) => set("limit", Number((e.target as HTMLSelectElement).value))}>
                {[50, 100, 250, 500].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label class="audit-text">
              <span class="label">{t("audit.textSearch")}</span>
              <input class="input" value={filters.value.text} placeholder={t("audit.textPlaceholder")}
                onInput={(e) => set("text", (e.target as HTMLInputElement).value)} />
            </label>
            <div class="audit-filter-actions">
              <button class="btn primary sm" disabled={loading.value}>{t("common.apply")}</button>
              <button type="button" class="btn ghost sm" onClick={() => { filters.value = emptyAuditFilters(); void load(); }}>{t("common.reset")}</button>
            </div>
          </form>
          <div class="audit-permission-row">
            <span class={`badge ${canViewSensitive.value ? "ok" : ""}`}>IP/UA {canViewSensitive.value ? t("status.visible") : t("status.hidden")}</span>
            <span class="badge">{visible.length} / {entries.value.length}</span>
          </div>
          {error.value && <div class="banner error">{error.value}</div>}
        </Panel>

        <Panel title={t("audit.events")} bodyClass="audit-table-body">
          <div class="audit-table-wrap">
            <table class="table audit-table">
              <thead>
                <tr>
                  <th>{t("audit.time")}</th><th>{t("audit.result")}</th><th>{t("field.action")}</th><th>{t("audit.actor")}</th><th>Server</th><th>{t("audit.target")}</th><th>{t("audit.summary")}</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr><td colSpan={7} class="audit-empty-cell">{loading.value ? t("audit.loading") : t("audit.empty")}</td></tr>
                ) : (
                  visible.map((e) => (
                    <tr
                      key={e.id}
                      class={`audit-row ${selected.value?.id === e.id ? "active" : ""}`}
                      onClick={() => (selected.value = e)}
                    >
                      <td class="mono nowrap">{formatAuditTs(e.ts)}</td>
                      <td><ResultChip result={e.result} /></td>
                      <td><ActionChip action={e.action} /></td>
                      <td>{e.actorUsername || "—"}</td>
                      <td>{e.serverId || "—"}</td>
                      <td class="mono">{targetLabel(e)}</td>
                      <td class="audit-summary-cell">{e.summary || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Panel>

        <DetailPanel entry={selected.value} canViewSensitive={canViewSensitive.value} />
      </div>
    </div>
  );
}
